import { useUBL } from '@common/log-manager';

import { ShopUxUbl } from '../types';

export type StoreHomeNavigationType = 'shop_detail' | 'shop_category_detail';

export type StoreHomeObjectSection = 'zonly' | 'best' | 'shop_collection_display' | 'all';

interface StoreHomePageviewData {
  is_opened: boolean;
  category_id?: string;
}

interface StoreHomeInformationPageviewData {
  sns_list: string;
}

export interface StoreHomeUBLBanner {
  id: string;
  type: string;
  url?: string;
  index: number;
}

export interface StoreHomeUBLCategory {
  id: string;
  name: string;
  index: number;
}

export interface StoreHomeUBLCatalog {
  id: string;
  index: number;
}

export interface StoreHomeInformationUBLSnsItem {
  name: string;
  url: string;
  index: number;
}

export const useStoreHomeUBL = (shop_id: string) => {
  const tracker = useUBL();

  const pageviewUBL = (navigation: StoreHomeNavigationType, page_url: string, data: StoreHomePageviewData) => {
    tracker({
      category: 'pageview',
      navigation,
      navigation_sub: { shop_id, page_url },
      data,
    });
  };

  const bannerClickUBL = async (banner: StoreHomeUBLBanner) => {
    tracker({
      category: 'click',
      navigation: 'shop_detail',
      navigation_sub: { shop_id },
      object_type: 'banner',
      object_id: banner.id,
      object_idx: banner.index,
      object_url: banner.url,
      data: {
        banner_type: banner.type,
      },
    });
  };

  const bannerImpressionUBL = async (banner: StoreHomeUBLBanner) => {
    tracker({
      category: 'impression',
      navigation: 'shop_detail',
      navigation_sub: { shop_id },
      object_type: 'banner',
      object_id: banner.id,
      object_idx: banner.index,
      data: {
        banner_type: banner.type,
      },
    });
  };

  const couponDownloadClickUBL = (ubl?: ShopUxUbl | null) => {
    tracker({
      category: 'click',
      navigation: 'shop_detail',
      navigation_sub: { shop_id },
      object_type: 'button',
      object_id: 'seller_coupon',
      data: ubl?.server_log ?? null,
    });
  };

  const couponImressionUBL = (ubl?: ShopUxUbl | null) => {
    tracker({
      category: 'impression',
      navigation: 'shop_detail',
      navigation_sub: { shop_id },
      object_type: 'button',
      object_id: 'seller_coupon',
      data: ubl?.server_log ?? null,
    });
  };

  const categoryClickUBL = (category: StoreHomeUBLCategory) => {
    tracker({
      category: 'click',
      navigation: 'shop_detail',
      navigation_sub: { shop_id },
      object_section: 'product_category',
      object_type: 'category',
      object_id: category.id,
      object_idx: category.index,
      data: {
        category: category.name,
      },
    });
  };

  const categoryImpressionUBL = (category: StoreHomeUBLCategory) => {
    tracker({
      category: 'impression',
      navigation: 'shop_detail',
      navigation_sub: { shop_id },
      object_section: 'product_category',
      object_type: 'category',
      object_id: category.id,
      object_idx: category.index,
      data: {
        category: category.name,
      },
    });
  };

  const shopNameMoreButtonClickUBL = () => {
    tracker({
      category: 'click',
      navigation: 'shop_detail',
      navigation_sub: { shop_id },
      object_type: 'button',
      object_id: 'shop_info_more',
    });
  };

  const shopInfoButtonClickUBL = () => {
    tracker({
      category: 'click',
      navigation: 'shop_detail',
      navigation_sub: { shop_id },
      object_type: 'button',
      object_id: 'shop_info_detail',
    });
  };

  const informationPageviewUBL = (data: StoreHomeInformationPageviewData) => {
    tracker({
      category: 'pageview',
      navigation: 'shop_info_detail',
      navigation_sub: { shop_id },
      data,
    });
  };

  const informationSnsLinkClickUBL = (sns_item: StoreHomeInformationUBLSnsItem) => {
    tracker({
      category: 'click',
      navigation: 'shop_info_detail',
      navigation_sub: { shop_id },
      object_type: 'button',
      object_id: sns_item.name,
      object_idx: sns_item.index,
      object_url: sns_item.url,
    });
  };

  const informationMoreModelInfoClickUBL = () => {
    tracker({
      category: 'click',
      navigation: 'shop_info_detail',
      navigation_sub: { shop_id },
      object_type: 'button',
      object_id: 'see_more_model_info',
    });
  };

  const impressionSellerGradeUBL = (ubl?: ShopUxUbl | null) => {
    tracker({
      category: 'impression',
      navigation: 'shop_detail',
      navigation_sub: { shop_id },
      object_type: 'image',
      object_id: 'shop_grade',
      data: ubl?.server_log ?? null,
    });
  };

  const searchClickUBL = () => {
    tracker({
      category: 'click',
      navigation: 'shop_detail',
      navigation_sub: { shop_id },
      object_type: 'button',
      object_id: 'search_bar',
    });
  };

  const zigzinFilterClickUBL = (is_selected: boolean) => {
    tracker({
      category: 'click',
      navigation: 'shop_detail',
      navigation_sub: { shop_id },
      object_type: 'button',
      object_section: 'sub_quick_filter',
      object_id: 'ZIGZIN',
      data: {
        is_selected,
      },
    });
  };

  return {
    pageviewUBL,
    bannerClickUBL,
    bannerImpressionUBL,
    couponDownloadClickUBL,
    categoryClickUBL,
    categoryImpressionUBL,
    informationPageviewUBL,
    informationSnsLinkClickUBL,
    informationMoreModelInfoClickUBL,
    zigzinFilterClickUBL,
    shopNameMoreButtonClickUBL,
    shopInfoButtonClickUBL,
    impressionSellerGradeUBL,
    couponImressionUBL,
    searchClickUBL,
  };
};
